import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { toLatinDigits } from "@/lib/wmDigits";
import { SC_TIMEOUT_MS, labelOf, sanitizeQuantity, scError, scMessage } from "./scUtils";

const D = OPS.sc.itemDialog;
const KINDS = ["branch", "warehouse"];
const PACK_MAX = 100000;
// SC_ITEM_CODE_TAKEN / SC_ITEM_ERP_TAKEN may come without `field`; they still belong under one input
const CODE_FIELD = { SC_ITEM_CODE_TAKEN: "code", SC_ITEM_ERP_TAKEN: "erp_code" };
const FIELDS = ["code", "erp_code", "name_ar", "name_en", "group", "kinds", "count_unit", "allows_decimal", "pack_unit",
    "pack_size", "manual_cost", "cost_unit", "cost_unit_size", "is_active", "sort_order", "note_ar"];

/** SAR with at most 4 decimals; Arabic-Indic digits and «٫» folded, separators dropped. */
function sanitizeCost(raw) {
    let s = toLatinDigits(raw).replace(/[^\d.]/g, "");
    const i = s.indexOf(".");
    if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "").slice(0, 4);
    return s;
}

const str = (v) => (v === null || v === undefined ? "" : String(v));

function fromItem(item) {
    return {
        code: str(item?.code),
        erp_code: str(item?.erp_code),
        name_ar: str(item?.name_ar),
        name_en: str(item?.name_en),
        group: str(item?.group),
        kinds: Array.isArray(item?.kinds) ? item.kinds : [...KINDS],
        count_unit: str(item?.count_unit),
        allows_decimal: item?.id ? !!item.allows_decimal : true,
        pack_unit: str(item?.pack_unit),
        pack_size: str(item?.pack_size),
        manual_cost: str(item?.manual_cost),
        cost_unit: str(item?.cost_unit),
        cost_unit_size: str(item?.cost_unit_size),
        is_active: item?.is_active !== false,
        sort_order: str(item?.sort_order),
        note_ar: str(item?.note_ar),
    };
}

/** Form -> the API body (SPEC §5 item fields). Blank optional text -> null. */
function toPayload(f) {
    const s = (v) => str(v).trim();
    const num = (v) => (s(v) === "" || s(v) === "." ? null : Number(s(v)));
    const packUnit = s(f.pack_unit) || null;
    const costUnit = s(f.cost_unit) || null;
    const sort = s(f.sort_order);
    return {
        code: s(f.code),
        erp_code: s(f.erp_code) || null,
        name_ar: s(f.name_ar),
        name_en: s(f.name_en),
        group: s(f.group),
        kinds: KINDS.filter((k) => f.kinds.includes(k)),
        count_unit: s(f.count_unit),
        allows_decimal: !!f.allows_decimal,
        pack_unit: packUnit,
        pack_size: packUnit ? num(f.pack_size) : null,
        manual_cost: num(f.manual_cost),
        cost_unit: costUnit,
        cost_unit_size: costUnit ? num(f.cost_unit_size) : null,
        is_active: !!f.is_active,
        sort_order: sort === "" || sort === "-" ? null : parseInt(sort, 10),
        note_ar: s(f.note_ar),
    };
}

/** The checks the server makes too, so the common mistakes show before a round trip. */
function validate(p) {
    const e = {};
    ["code", "name_ar", "group", "count_unit"].forEach((k) => { if (!p[k]) e[k] = "stk_items_err_required"; });
    if (!p.kinds.length) e.kinds = "stk_items_err_kinds";
    if (p.pack_unit && !(p.pack_size > 1 && p.pack_size <= PACK_MAX)) e.pack_size = "stk_items_err_pack_size";
    if (p.manual_cost !== null && !(p.manual_cost >= 0)) e.manual_cost = "stk_items_err_cost";
    if (p.cost_unit && !(p.cost_unit_size > 0)) e.cost_unit_size = "stk_items_err_cost_unit_size";
    return e;
}

function Field({ k, label, required, error, children, className = "" }) {
    return (
        <div className={`min-w-0 space-y-1 ${className}`}>
            <Label className="text-xs font-bold text-[color:var(--ink)]">
                {label}{required ? <span className="text-[color:var(--danger)]"> *</span> : null}
            </Label>
            {children}
            {error ? <p data-field-error={k} className="text-[11px] font-semibold text-[color:var(--danger)]">{error}</p> : null}
        </div>
    );
}

/** Add / edit one stock-count item (finance). Every SPEC §5 field; a PATCH sends
 *  only the changed keys (the audit lists exactly those). Server field errors
 *  (`detail.field`) are shown under their input. */
export function ItemDialog({ item, scope, groups, onClose, onSaved }) {
    const { t, lang } = useI18n();
    const isNew = !item?.id;
    const [form, setForm] = useState(() => fromItem(item));
    const [errors, setErrors] = useState({});   // field -> message (already translated)
    const [busy, setBusy] = useState(false);
    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; });
    };
    const toggleKind = (k, on) => set("kinds", on ? [...new Set([...form.kinds, k])] : form.kinds.filter((x) => x !== k));
    const invalid = (k) => (errors[k] ? "border-[color:var(--danger)]" : "");
    const kindLabels = scope?.labels?.kinds;

    const save = async () => {
        const payload = toPayload(form);
        const local = validate(payload);
        if (Object.keys(local).length) {
            const m = {};
            Object.entries(local).forEach(([k, key]) => { m[k] = t(key); });
            setErrors(m);
            toast.error(t("stk_items_fix_fields"));
            return;
        }
        if (payload.sort_order === null || Number.isNaN(payload.sort_order)) delete payload.sort_order;
        let body = payload;
        if (!isNew) {
            const before = toPayload(fromItem(item));
            body = {};
            Object.keys(payload).forEach((k) => {
                if (JSON.stringify(payload[k]) !== JSON.stringify(before[k])) body[k] = payload[k];
            });
            // a unit and its size are validated together: send both when either changed
            [["pack_unit", "pack_size"], ["cost_unit", "cost_unit_size"]].forEach(([a, b]) => {
                if (a in body || b in body) { body[a] = payload[a]; body[b] = payload[b]; }
            });
            if (!Object.keys(body).length) { toast(t("stk_items_no_changes"), { id: "sc-item-no-changes" }); onClose(); return; }
        }
        setBusy(true);
        setErrors({});
        try {
            const r = isNew
                ? await api.post("/sc/items", body, { timeout: SC_TIMEOUT_MS })
                : await api.patch(`/sc/items/${item.id}`, body, { timeout: SC_TIMEOUT_MS });
            toast.success(t("stk_items_saved"));
            onSaved && (await onSaved(r.data));
        } catch (e) {
            const d = scError(e);
            const field = d?.field || CODE_FIELD[d?.code];
            if (d && FIELDS.includes(field)) setErrors({ [field]: lang === "ar" ? d.message_ar : d.message_en });
            toast.error(scMessage(e, lang) || t("stk_admin_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    const input = "h-11 text-base";
    return (
        <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
            <DialogContent data-testid={D.dialog} className="max-h-[88vh] max-w-lg overflow-y-auto bg-[color:var(--bg-surface)] scrollbar-thin">
                <DialogHeader className="text-start sm:text-start">
                    <DialogTitle>{t(isNew ? "stk_items_add" : "stk_items_edit")}</DialogTitle>
                    <DialogDescription>{t("stk_items_dialog_hint")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <section className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Field k="code" label={t("stk_items_code")} required error={errors.code}>
                                <Input data-testid={D.field("code")} value={form.code} dir="ltr" maxLength={40} autoComplete="off"
                                       onChange={(e) => set("code", e.target.value)} className={`${input} ${invalid("code")}`} />
                            </Field>
                            <Field k="erp_code" label={t("stk_items_erp_code")} error={errors.erp_code}>
                                <Input data-testid={D.field("erp_code")} value={form.erp_code} dir="ltr" maxLength={40} autoComplete="off"
                                       placeholder="PT-…" onChange={(e) => set("erp_code", e.target.value)} className={`${input} ${invalid("erp_code")}`} />
                            </Field>
                        </div>
                        <Field k="name_ar" label={t("stk_items_name_ar")} required error={errors.name_ar}>
                            <Input data-testid={D.field("name_ar")} value={form.name_ar} dir="rtl" maxLength={120}
                                   onChange={(e) => set("name_ar", e.target.value)} className={`${input} ${invalid("name_ar")}`} />
                        </Field>
                        <Field k="name_en" label={t("stk_items_name_en")} error={errors.name_en}>
                            <Input data-testid={D.field("name_en")} value={form.name_en} dir="ltr" maxLength={120}
                                   onChange={(e) => set("name_en", e.target.value)} className={`${input} ${invalid("name_en")}`} />
                        </Field>
                        <Field k="group" label={t("stk_items_group")} required error={errors.group}>
                            <Input data-testid={D.field("group")} value={form.group} maxLength={60} list="sc-item-groups"
                                   onChange={(e) => set("group", e.target.value)} className={`${input} ${invalid("group")}`} />
                            <datalist id="sc-item-groups">
                                {(groups || []).map((g) => <option key={g} value={g} />)}
                            </datalist>
                        </Field>
                        <Field k="kinds" label={t("stk_items_kinds")} required error={errors.kinds}>
                            <div className="flex flex-wrap gap-2">
                                {KINDS.map((k) => (
                                    <label key={k} data-testid={D.kind(k)}
                                           className="flex h-11 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-3">
                                        <Checkbox checked={form.kinds.includes(k)} onCheckedChange={(v) => toggleKind(k, !!v)} />
                                        <span className="text-sm text-[color:var(--ink)]">{labelOf(kindLabels, k, lang)}</span>
                                    </label>
                                ))}
                            </div>
                        </Field>
                    </section>

                    <section className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] p-3">
                        <h4 className="text-xs font-bold text-[color:var(--ink-muted)]">{t("stk_items_section_count")}</h4>
                        <div className="grid grid-cols-2 gap-3">
                            <Field k="count_unit" label={t("stk_items_count_unit")} required error={errors.count_unit}>
                                <Input data-testid={D.field("count_unit")} value={form.count_unit} maxLength={40}
                                       onChange={(e) => set("count_unit", e.target.value)} className={`${input} ${invalid("count_unit")}`} />
                            </Field>
                            <Field k="allows_decimal" label={t("stk_items_allows_decimal")} error={errors.allows_decimal}>
                                <label className="flex h-11 items-center gap-2">
                                    <Switch data-testid={D.field("allows_decimal")} checked={form.allows_decimal}
                                            onCheckedChange={(v) => set("allows_decimal", !!v)} />
                                    <span className="text-xs text-[color:var(--ink-muted)]">{t(form.allows_decimal ? "stk_items_decimal_yes" : "stk_items_decimal_no")}</span>
                                </label>
                            </Field>
                            <Field k="pack_unit" label={t("stk_items_pack_unit")} error={errors.pack_unit}>
                                <Input data-testid={D.field("pack_unit")} value={form.pack_unit} maxLength={60}
                                       placeholder={t("stk_items_pack_unit_ph")}
                                       onChange={(e) => set("pack_unit", e.target.value)} className={`${input} ${invalid("pack_unit")}`} />
                            </Field>
                            <Field k="pack_size" label={t("stk_items_pack_size")} required={!!form.pack_unit.trim()} error={errors.pack_size}>
                                <div className="relative" dir="ltr">
                                    <Input data-testid={D.field("pack_size")} value={form.pack_size} dir="ltr" inputMode="decimal"
                                           disabled={!form.pack_unit.trim()} autoComplete="off"
                                           onChange={(e) => set("pack_size", sanitizeQuantity(e.target.value, true))}
                                           className={`${input} pe-14 tabular-nums ${invalid("pack_size")}`} />
                                    <span className="pointer-events-none absolute inset-y-0 end-3 flex max-w-[3rem] items-center truncate text-xs font-medium text-[color:var(--ink-muted)]">
                                        {form.count_unit}
                                    </span>
                                </div>
                            </Field>
                        </div>
                        <p className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">{t("stk_items_pack_hint")}</p>
                    </section>

                    <section className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] p-3">
                        <h4 className="text-xs font-bold text-[color:var(--ink-muted)]">{t("stk_items_section_cost")}</h4>
                        <Field k="manual_cost" label={t("stk_items_manual_cost")} error={errors.manual_cost}>
                            <div className="relative" dir="ltr">
                                <Input data-testid={D.field("manual_cost")} value={form.manual_cost} dir="ltr" inputMode="decimal"
                                       autoComplete="off" placeholder="—"
                                       onChange={(e) => set("manual_cost", sanitizeCost(e.target.value))}
                                       className={`${input} pe-14 tabular-nums ${invalid("manual_cost")}`} />
                                <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-xs font-medium text-[color:var(--ink-muted)]">
                                    {lang === "ar" ? "ر.س" : "SAR"}
                                </span>
                            </div>
                        </Field>
                        <p className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">{t("stk_items_manual_cost_hint")}</p>
                        <div className="grid grid-cols-2 gap-3">
                            <Field k="cost_unit" label={t("stk_items_cost_unit")} error={errors.cost_unit}>
                                <Input data-testid={D.field("cost_unit")} value={form.cost_unit} maxLength={60}
                                       onChange={(e) => set("cost_unit", e.target.value)} className={`${input} ${invalid("cost_unit")}`} />
                            </Field>
                            <Field k="cost_unit_size" label={t("stk_items_cost_unit_size")} required={!!form.cost_unit.trim()} error={errors.cost_unit_size}>
                                <Input data-testid={D.field("cost_unit_size")} value={form.cost_unit_size} dir="ltr" inputMode="decimal"
                                       disabled={!form.cost_unit.trim()} autoComplete="off"
                                       onChange={(e) => set("cost_unit_size", sanitizeQuantity(e.target.value, true))}
                                       className={`${input} tabular-nums ${invalid("cost_unit_size")}`} />
                            </Field>
                        </div>
                        <p className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">{t("stk_items_cost_unit_hint")}</p>
                    </section>

                    <section className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Field k="sort_order" label={t("stk_items_sort_order")} error={errors.sort_order}>
                                <Input data-testid={D.field("sort_order")} value={form.sort_order} dir="ltr" inputMode="numeric" autoComplete="off"
                                       onChange={(e) => set("sort_order", toLatinDigits(e.target.value).replace(/(?!^-)[^\d]/g, ""))}
                                       className={`${input} tabular-nums ${invalid("sort_order")}`} />
                            </Field>
                            <Field k="is_active" label={t("stk_items_active")} error={errors.is_active}>
                                <label className="flex h-11 items-center gap-2">
                                    <Switch data-testid={D.field("is_active")} checked={form.is_active}
                                            onCheckedChange={(v) => set("is_active", !!v)} />
                                    <span className="text-xs text-[color:var(--ink-muted)]">{t(form.is_active ? "stk_admin_active" : "stk_admin_inactive")}</span>
                                </label>
                            </Field>
                        </div>
                        <Field k="note_ar" label={t("stk_items_note")} error={errors.note_ar}>
                            <Textarea data-testid={D.field("note_ar")} value={form.note_ar} maxLength={500} rows={2}
                                      onChange={(e) => set("note_ar", e.target.value)} className={`text-base ${invalid("note_ar")}`} />
                        </Field>
                    </section>
                </div>

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button type="button" variant="outline" data-testid={D.cancel} disabled={busy} onClick={onClose}>{t("stk_admin_cancel")}</Button>
                    <Button type="button" data-testid={D.save} disabled={busy} onClick={save}>
                        {busy ? t("stk_admin_saving") : t("stk_admin_save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
